<template>
  <page>
    <lazy-div :load="doInit">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="newMode">mdi-plus</v-icon>
          <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
          <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
          <v-toolbar-title v-if="newMode">Create New Risk Factor</v-toolbar-title>
          <v-toolbar-title v-else-if="reviewMode">Edit Risk Factor</v-toolbar-title>
          <v-toolbar-title v-else-if="queryMode">View Existing Risk Factor</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container grid-list-md>
            <v-layout row wrap>
              <v-flex xs6 v-if="newMode">
                <v-text-field
                  v-model="riskFactForm.factor"
                  :label="riskFactLabel.factor"
                  :error-messages="errors.collect('factor')"
                  data-vv-name="factor"
                  :data-vv-as="riskFactLabel.factor"
                  v-validate="'required|max:4'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ riskFactLabel.factor }}</v-subheader>
                <div class="queryValue">{{ riskFactForm.factor }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  v-model="riskFactForm.desc"
                  :label="riskFactLabel.desc"
                  :error-messages="errors.collect('desc')"
                  data-vv-name="desc"
                  :data-vv-as="riskFactLabel.desc"
                  v-validate="'required|max:40'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ riskFactLabel.desc }}</v-subheader>
                <div class="queryValue">{{ riskFactForm.desc }}</div>
              </v-flex>
              <v-flex xs2>
                <v-checkbox color="primary" :label="riskFactLabel.allLookUpAE" v-model="riskFactForm.allLookUpAE" :disabled="queryMode"></v-checkbox>
              </v-flex>
              <v-flex xs2>
                <v-checkbox
                  color="primary"
                  :label="riskFactLabel.isStatisticAE"
                  v-model="riskFactForm.isStatisticAE"
                  :disabled="queryMode"
                ></v-checkbox>
              </v-flex>
              <v-flex xs2>
                <v-checkbox color="primary" :label="riskFactLabel.isNumAE" v-model="riskFactForm.isNumAE" :disabled="queryMode"></v-checkbox>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ riskFactLabel.createdBy }}</v-subheader>
                <div class="queryValue">{{ riskFactForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ riskFactLabel.createdDate }}</v-subheader>
                <div class="queryValue">{{ riskFactForm.createdDate | formatDate }}</div>
              </v-flex>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ riskFactLabel.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ riskFactForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ riskFactLabel.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ riskFactForm.modifiedDate | formatDate }}</div>
              </v-flex>
            </v-layout>
            <v-spacer class="mt-3"></v-spacer>
            <v-layout>
              <v-flex xs6>
                <v-btn color="red darken-1" dark class=" mr-2" @click.native="doCancel">
                  Cancel
                  <v-icon right dark>mdi-cancel</v-icon>
                </v-btn>
                <v-btn v-if="!queryMode" color="primary" dark class=" mr-2" @click="doSave" :loading="submitting">
                  Save
                  <v-icon right>mdi-content-save</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import clone from 'clone'
import { expandURI } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'

export default {
  data() {
    return {
      submitting: false,
      newMode: false,
      reviewMode: false,
      queryMode: false,
      riskFactLabel: {
        factor: 'Factor',
        desc: 'Description',
        allLookUpAE: 'All Lookup',
        isStatisticAE: 'Is Statistic',
        isNumAE: 'Is Number',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      riskFactForm: {
        factor: null,
        desc: null,
        allLookUpAE: false,
        isStatisticAE: false,
        isNumAE: false,
        createdBy: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
        createdDate: new Date(),
        modifiedBy: null,
        modifiedDate: null
      }
    }
  },
  methods: {
    async doInit() {
      if (!this.newRiskFact) {
        this.fetchData()
      }
    },
    async fetchData() {
      const apiURI = this.menu.getRiskFact
      if (apiURI) {
        try {
          const expand = {
            factor: this.riskFactId.factor
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.riskFactForm.factor = data.riskFact.factor
            this.riskFactForm.desc = data.riskFact.desc
            this.riskFactForm.allLookUpAE = data.riskFact.allLookUpAE === 'true'
            this.riskFactForm.isStatisticAE = data.riskFact.isStatisticAE === 'true'
            this.riskFactForm.isNumAE = data.riskFact.isNumAE === 'true'
            this.riskFactForm.createdBy = data.createdBy
            this.riskFactForm.createdDate = data.createdDate
            this.riskFactForm.modifiedBy = data.modifiedBy
            this.riskFactForm.modifiedDate = data.modifiedDate
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doCancel() {
      this.$router.push({ name: 'riskFactor' })
    },
    doSave() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newRiskFact) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    doCreate() {
      const apiURI = this.menu.createRiskFact
      if (apiURI) {
        let messageFilters = clone(this.riskFactForm)
        this.$http
          .post(apiURI, messageFilters)
          .then((result) => {
            const { data } = result
            if (data) {
              this.$router.push({ name: 'riskFactor' })
              this.notify(data)
            }
          })
          .catch((err) => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    async doUpdate() {
      const apiURI = this.menu.updateRiskFact
      if (apiURI) {
        this.submitting = true
        let messageFilters = clone(this.riskFactForm)

        try {
          const result = await this.$http.put(apiURI, messageFilters)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'riskFactor' })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    }
  },
  created() {
    const jsonData = JSON.parse(sessionStorage.getItem('riskFact'))
    this.$store.commit('RISK_FACTOR_COMMIT_KEY', jsonData)

    if (this.riskFactId.tarfFactMode !== '') {
      if (this.riskFactId.riskFactMode === 'New') {
        this.newMode = true
      } else if (this.riskFactId.riskFactMode === 'Review') {
        this.reviewMode = true
      } else if (this.riskFactId.riskFactMode === 'Query') {
        this.queryMode = true
      }
    }
  },
  computed: {
    menu() {
      return this.$store.state.riskFactorParam.menu
    },
    newRiskFact() {
      return this.riskFactId.riskFactMode === 'New'
    },
    riskFactId() {
      return this.$store.state.riskFactorParam.riskFact.riskFactId
    }
  },
  filters: {
    formatDate(value) {
      return formatDisplayDate(value)
    }
  },
  mixins: [MessageMixin]
}
</script>
